import MercuryTabs from '@/components/content/tabs/Tabs';
import MercuryTitle from '@/components/content/title/Title';
import TablePlaceholder from '@/components/content/table/TablePlaceholder';
import TableInstance from '@/components/content/table/TableInstance';
import { normalizeSignal, useSignals } from '@/client/features/signals/services/signals';
import { useEventRelationshipsTableState } from '@/client/features/events/hooks/useEventRelationshipsTableState';
import { signalColumns } from '@/client/features/events/configs/eventrelationships.table';
import { ArticleItem } from '@/client/features/events/types';
import { dedupeByField } from '@/utils';

interface EntityRelationShipsProps {
  renderTabsSelect?: boolean;
  articles?: ArticleItem[];
}

const buildBaseFilter = (articles?: ArticleItem[]): string => {
  if (!articles?.length) return '';

  const ids = Array.from(
    new Set(articles.map((a) => a.signal_id?.[0]).filter((id): id is string => Boolean(id)))
  );

  if (!ids.length) return '';

  return `id:(${ids.join(' or ')})`;
};

const EntityRelationShips = ({ renderTabsSelect, articles }: EntityRelationShipsProps) => {
  const { pageIndex: signalsPageIndex, instanceConfiguration: signalsTable } =
    useEventRelationshipsTableState(signalColumns);

  const LENGTH = 10;
  const baseFilter = [buildBaseFilter(dedupeByField('signal_id', articles ?? []))];

  const signals = useSignals({
    filter: baseFilter,
    start: signalsPageIndex * LENGTH,
    length: LENGTH,
  }).list((data) => ({
    count: data.data?.count,
    signals: data.data?.records?.map(normalizeSignal),
  }));

  const isTabDisabled = (count: number | undefined, isError: boolean, errorCode?: number) =>
    (!count || isError) && errorCode !== 402;

  const getTabCount = (
    dataCount: number | undefined,
    error: { code?: number; details?: Record<string, any> } | null | undefined
  ): number | '?' => {
    if (error?.code === 402) {
      const count = error.details?.count;
      if (typeof count === 'number') return count;
      return '?';
    }
    return dataCount ?? '?';
  };

  const tabs = [
    {
      tab: `Signals (${getTabCount(signals.data?.count, signals.error)})`,
      val: 'signals',
      disabled: isTabDisabled(signals.data?.count, signals.isError, signals.error?.code),
    },
    {
      tab: 'Clusters (0)',
      val: 'clusters',
      disabled: true,
    },
    {
      tab: 'Raw Intelligence (0)',
      val: 'raw-intelligence',
      disabled: true,
    },
    {
      tab: 'Finished Intelligence (0)',
      val: 'finished-intelligence',
      disabled: true,
    },
  ];

  return (
    <div>
      <MercuryTitle size="h4" order={2}>
        QuoIntelligence Related Intel
      </MercuryTitle>

      <MercuryTabs mt={25} tabs={tabs} renderSelect={renderTabsSelect} small>
        <TablePlaceholder isPending={signals.isPending} rows={5}>
          <TableInstance
            data={signals.data?.signals || []}
            instanceConfiguration={signalsTable}
            pageCount={signals.data?.count ?? 0}
            id="entity-relationships-table-signals"
            isLoading={signals.isFetching}
            options={{ hideToolbar: true, hidePageSize: true }}
            showUpsellPlaceholder={signals.isError && signals.error?.code === 402}
          />
        </TablePlaceholder>
      </MercuryTabs>
    </div>
  );
};

export default EntityRelationShips;
